import type React from "react";
import {
  extractFieldName,
  findFieldSlug,
  getFormLabelsBySlug,
  isDynamicField,
  isPrefillKey,
} from "../helpers/prefill";
import { normalizePrefillFieldValue } from "../registry/map";
import type { FormConfig, FormConfigApplicationStructureItem } from "../types/form";
import { PrefillContext } from "./usePrefillContext";

interface PrefillProviderProps {
  applicationStructure: FormConfigApplicationStructureItem[];
  config: FormConfig;
  children: React.ReactNode;
}

export const PrefillProvider = ({
  applicationStructure,
  children,
  config,
}: PrefillProviderProps): React.ReactElement => {
  const query = new URLSearchParams(window.location.search);

  const prefillValues = Array.from(query.entries()).reduce(
    (acc, [key, value]) => {
      if (!isPrefillKey(key)) return acc;

      const fieldName = extractFieldName(key);
      const formLabelsBySlug = getFormLabelsBySlug(config, applicationStructure);
      const fieldSlug = findFieldSlug(formLabelsBySlug, fieldName);
      const field = applicationStructure.find((field) => field.slug === fieldSlug);

      if (!field) return acc;

      if (isDynamicField(field.field_type)) {
        return {
          ...acc,
          dynamicFields: { ...acc.dynamicFields, [field.slug]: value },
        };
      }

      const normalizedValue = normalizePrefillFieldValue({
        value,
        field,
        config,
      });

      return {
        ...acc,
        simpleFields: { ...acc.simpleFields, [field.slug]: normalizedValue },
      };
    },
    {
      simpleFields: {},
      dynamicFields: {},
    },
  );

  return (
    <PrefillContext.Provider value={{ prefillValues, config }}>{children}</PrefillContext.Provider>
  );
};
